import hash from "object-hash";
import PropTypes from "prop-types";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  DropdownButton,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import { useCookies } from "react-cookie";
import {
  FaAngleLeft,
  FaAngleRight,
  FaColumns,
  FaRegCheckSquare,
  FaRegSquare,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaSync,
  FaTimes,
} from "react-icons/fa";
import Api from "../services/api/index";
import "./BootstrapTable.css";
import IconText from "./IconText";

const BootstrapTable = forwardRef(
  (
    {
      id, // id of this table. if empty, an id is created by hashing the url
      url, // REST-URL
      columns: defaultColumns,
      sort = "", // field to sort by
      order = "asc", // sort order (asc or desc)
      pageSize = 15, // selected page size
      pageSizes = [10, 15, 25, 50, 100], // available page sizes to choose from
      cache = false, // if true, state is saved in cookies and restored on page refresh
      searchPlaceholder = "suchen...", // Placeholder of the search input field
      searchTimeout = 700, // Delay in ms before search is triggered
      showClear = false, // show the clear-icon
      showSearch = false, // show search imput field
      showFilter = false, // show column filters
      showFooter = false, // show a footer
      showColumnToggle = false, // show hide/show function for columns
      showRefresh = false, // show refresh button
      hidePageSize = false, // show page size changer in toolbar
      hidePageCount = false, // show page count in toolbar
      hidePagination = false, // disable the pagination
      showLoading = false, // show loading between data refreshes
      toolbar = null, // Custom code to be added on the left side of the toolbar
    },
    ref
  ) => {
    const tableId = id === undefined ? hash(url) : id;

    const searchInput = useRef();
    const filterInputs = useRef([]);
    const [cookies, setCookie, removeCookie] = useCookies([`table.${tableId}`]);

    // Timer for search delay
    const [timer, setTimer] = useState(null);

    // Indicates wheter data is being fetched from server
    const [isLoading, setIsLoading] = useState(false);

    const [columns, setColumns] = useState(defaultColumns);

    // All properties that triggers a refresh of the table
    const [controlData, setControlData] = useState(() => {
      const initialState = {
        currentPage: 1,
        pageSize,
        sort,
        order,
        search: "",
        filter: {},
      };

      if (cache === true) {
        // Load state from cookie
        const cookieValues = cookies[`table.${tableId}`];
        if (cookieValues !== null) {
          return { ...initialState, ...cookieValues };
        }
      }

      return initialState;
    });

    // Data returned from server
    const [data, setData] = useState({ total: 0, rows: [] });

    const showColumn = (field) => {
      const tempColumns = [...columns];

      tempColumns.forEach((element, index) => {
        if (element.field === field) {
          tempColumns[index].hidden = false;
        }
      });

      setColumns(tempColumns);
    };

    const hideColumn = (field) => {
      const tempColumns = [...columns];

      tempColumns.forEach((element, index) => {
        if (element.field === field) {
          tempColumns[index].hidden = true;
        }
      });

      setColumns(tempColumns);
    };

    const toggleColumn = (field) => {
      const tempColumns = [...columns];

      tempColumns.forEach((element, index) => {
        if (element.field === field) {
          tempColumns[index].hidden = !element.hidden;
        }
      });

      setColumns(tempColumns);
    };

    const handleClear = () => {
      console.log("Clear called");
      removeCookie(`table.${tableId}`);

      if (searchInput != null && searchInput.current != null) {
        searchInput.current.value = "";
      }

      filterInputs.current.forEach((fieldItem) => {
        if (fieldItem != null) {
          fieldItem.value = "";
        }
      });

      const tmpControlData = { ...controlData };
      tmpControlData.currentPage = 1;
      tmpControlData.pageSize = pageSize;
      tmpControlData.sort = sort;
      tmpControlData.order = order;
      tmpControlData.search = "";
      tmpControlData.filter = {};
      setControlData(tmpControlData);
    };

    const handleSort = (newSort) => {
      console.log("Sort called");
      const tmpControlData = { ...controlData };
      if (tmpControlData.sort === newSort) {
        tmpControlData.order = tmpControlData.order === "asc" ? "desc" : "asc";
      } else {
        tmpControlData.sort = newSort;
        tmpControlData.order = "asc";
      }
      setControlData(tmpControlData);
    };

    const handlePageSize = (newPageSize) => {
      console.log("PageSize called");

      const tmpControlData = { ...controlData };
      tmpControlData.pageSize = newPageSize;
      setControlData(tmpControlData);
    };

    const handleSearch = (searchValue) => {
      if (timer) {
        clearTimeout(timer);
        setTimer(null);
      }
      setTimer(
        setTimeout(() => {
          const tmpControlData = { ...controlData };
          tmpControlData.search = searchValue;
          tmpControlData.currentPage = 1;
          setControlData(tmpControlData);
        }, searchTimeout)
      );
    };

    const handleFilter = (field, value) => {
      // console.log("handleFilter", field, value);
      if (timer) {
        clearTimeout(timer);
        setTimer(null);
      }
      setTimer(
        setTimeout(() => {
          const tmpControlData = { ...controlData };
          tmpControlData.filter[field] = value;
          tmpControlData.currentPage = 1;
          setControlData(tmpControlData);
        }, searchTimeout)
      );
    };

    const handleNextPage = () => {
      const tmpControlData = { ...controlData };
      tmpControlData.currentPage += 1;
      if (
        tmpControlData.currentPage >
        Math.ceil(data.total / tmpControlData.pageSize)
      ) {
        tmpControlData.currentPage = Math.ceil(
          data.total / tmpControlData.pageSize
        );
      }
      setControlData(tmpControlData);
    };

    const handlePreviousePage = () => {
      const tmpControlData = { ...controlData };
      tmpControlData.currentPage -= 1;
      if (tmpControlData.currentPage < 1) {
        tmpControlData.currentPage = 1;
      }
      setControlData(tmpControlData);
    };

    const goToPage = (page) => {
      const tmpControlData = { ...controlData };
      tmpControlData.currentPage = page;
      setControlData(tmpControlData);
    };

    const getHeaderGroupRow = () => {
      const groupRows = [];
      let lastGroupName;
      let spanCount = 0;
      columns.forEach((col) => {
        if (col.hidden !== true) {
          if (lastGroupName !== col.groupName && lastGroupName !== undefined) {
            groupRows.push({ name: lastGroupName, colSpan: spanCount });
            spanCount = 0;
          }
          spanCount += 1;
          lastGroupName = col.groupName;
        }
      });
      groupRows.push({ name: lastGroupName, colSpan: spanCount });

      // console.log(groupRows);
      if (groupRows.length <= 1) {
        return null;
      }

      return (
        <tr>
          {groupRows.map((col) => {
            return (
              <th
                key={hash(col)}
                style={{ borderTop: "none" }}
                colSpan={col.colSpan}
              >
                {typeof col.name === "function" ? col.name() : col.name}
              </th>
            );
          })}
        </tr>
      );
    };

    // Generate the Header Columns
    const getHeaderRow = () => (
      <tr>
        {columns.map((col) => {
          if (col.hidden === true) {
            return null;
          }

          const columnStyle = {
            borderTop: "none",
          };

          if (col.width) {
            columnStyle.width = col.width;
          }

          let sortLabel = "";
          if (col.sortable === true) {
            if (controlData.sort === col.field) {
              if (controlData.order === "asc") {
                sortLabel = <FaSortDown />;
              } else {
                sortLabel = <FaSortUp />;
              }
            } else {
              sortLabel = (
                <FaSort
                  style={{
                    color: "#cccccc",
                  }}
                />
              );
            }

            columnStyle.cursor = "pointer";

            return (
              <th
                key={hash(col)}
                onClick={() => handleSort(col.field)}
                style={columnStyle}
              >
                <Row
                  noGutters
                  style={{ flexWrap: "nowrap" }}
                  className="align-items-end"
                >
                  <Col>
                    {typeof col.name === "function" ? col.name() : col.name}
                  </Col>
                  <Col xs="auto">{sortLabel}</Col>
                </Row>
              </th>
            );
          }

          return (
            <th key={hash(col)} style={columnStyle}>
              {typeof col.name === "function" ? col.name() : col.name}
            </th>
          );
        })}
      </tr>
    );

    // Generate the Header row
    const getHeaderFilterRow = () => {
      if (showFilter === false) {
        return null;
      }
      return (
        <tr>
          {columns.map((col, index) => {
            if (col.hidden === true) {
              return null;
            }

            let field = "";
            if (col.filterControl === "input") {
              field = (
                <input
                  style={{ width: "100%" }}
                  ref={(el) => (filterInputs.current[index] = el)}
                  onChange={(event) =>
                    handleFilter(col.field, event.target.value)
                  }
                />
              );
            }

            return <th style={{ borderTop: "none", padding: 0 }}>{field}</th>;
          })}
        </tr>
      );
    };

    // Generate the Header row
    const getFooterRow = () => {
      if (showFooter === false) {
        return null;
      }
      return (
        <tfoot style={{ fontWeight: "bold", borderTop: "2px solid #dee2e6" }}>
          <tr>
            {columns.map((col) => {
              if (col.hidden === true) {
                return null;
              }

              let value = "";
              if (col.footerFormatter) {
                value = col.footerFormatter(data.rows, col.field);
              }

              const rowStyle = {};

              // Set text alignment
              if (col.textAlign) {
                rowStyle.style = {
                  ...rowStyle.style,
                  textAlign: col.textAlign,
                };
              }

              return <td {...rowStyle}>{value}</td>;
            })}
          </tr>
        </tfoot>
      );
    };

    const getPagination = () => {
      const lastPage = Math.floor(data.total / controlData.pageSize) + 1;

      const pages = [];
      if (lastPage <= 1) {
        // Do nothing
      } else if (lastPage <= 5) {
        for (let i = 1; i <= lastPage; i += 1) {
          pages.push(i);
        }
      } else if (controlData.currentPage < 5) {
        for (let i = 1; i <= 5; i += 1) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(lastPage);
      } else if (controlData.currentPage > lastPage - 5) {
        pages.push(1);
        pages.push("...");
        for (let i = lastPage - 5; i <= lastPage; i += 1) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (
          let i = controlData.currentPage - 1;
          i <= controlData.currentPage + 1;
          i += 1
        ) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(lastPage);
      }

      return (
        <ButtonGroup>
          {controlData.currentPage > 1 && (
            <Button
              variant="outline-secondary"
              onClick={() => handlePreviousePage()}
            >
              <FaAngleLeft />
            </Button>
          )}
          {pages.map((i) => {
            if (i === "...") {
              return (
                <Button variant="outline-secondary" disabled>
                  {i}
                </Button>
              );
            }
            return (
              <Button
                variant={
                  controlData.currentPage === i
                    ? "secondary"
                    : "outline-secondary"
                }
                onClick={() => goToPage(i)}
              >
                {i}
              </Button>
            );
          })}
          {controlData.currentPage < lastPage && (
            <Button
              variant="outline-secondary"
              onClick={() => handleNextPage(lastPage)}
            >
              <FaAngleRight />
            </Button>
          )}
        </ButtonGroup>
      );
    };

    const loadData = async () => {
      setIsLoading(true);
      let searchUrl = url;
      if (url !== null && url.indexOf("?", 0) !== -1) {
        searchUrl += `&limit=${controlData.pageSize}`;
      } else {
        searchUrl += `?limit=${controlData.pageSize}`;
      }

      searchUrl += `&offset=${
        controlData.pageSize * (controlData.currentPage - 1)
      }`;

      if (controlData.sort.length > 0) {
        searchUrl += `&sort=${controlData.sort}`;
        searchUrl += `&order=${controlData.order}`;
      }

      if (Object.keys(controlData.filter).length > 0) {
        searchUrl += `&filter=${JSON.stringify(controlData.filter)}`;
      }

      if (controlData.search.length > 0) {
        searchUrl += `&search=${controlData.search}`;
      }

      // Load data from API
      const apiData = await Api.getTableData(searchUrl);
      setData(apiData.data);
      console.log(apiData.data);

      setIsLoading(false);

      // Save state in cooke if activated
      if (cache === true) {
        setCookie(`table.${tableId}`, controlData, {
          path: "/",
          sameSite: "strict",
          secure: true,
        });
      }
    };

    const handleRefresh = () => {
      console.log("Refresh called");
      loadData();
    };

    // Funktionen die von aussen via ref aufgerufen werden können
    useImperativeHandle(
      ref,
      () => ({
        refresh() {
          handleRefresh();
        },
        showColumn(field) {
          showColumn(field);
        },
        hideColumn(field) {
          hideColumn(field);
        },
        toggleColumn(field) {
          toggleColumn(field);
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    // Fetch data from server when component is being rendered or controlData changed
    useEffect(() => {
      loadData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlData, cache, setCookie, tableId, url]);

    useEffect(() => {
      setColumns(defaultColumns);
    }, [defaultColumns]);

    return (
      <React.Fragment>
        {(toolbar ||
          showSearch ||
          showRefresh ||
          showClear ||
          showColumnToggle) && (
          <Row className="add-margin" style={{ fontSize: "14px" }}>
            <Col md={6}>{toolbar}</Col>
            <Col md={6}>
              <InputGroup>
                {showSearch && (
                  <Form.Control
                    type="text"
                    defaultValue={controlData.search}
                    placeholder={searchPlaceholder}
                    ref={searchInput}
                    onChange={(event) => handleSearch(event.target.value)}
                  />
                )}

                {(showRefresh || showClear || showColumnToggle) && (
                  <ButtonGroup as={InputGroup.Append}>
                    {showClear && (
                      <Button variant="default" onClick={handleClear}>
                        <FaTimes />
                      </Button>
                    )}
                    {showRefresh && (
                      <Button variant="default" onClick={handleRefresh}>
                        <FaSync />
                      </Button>
                    )}

                    {showColumnToggle && (
                      <DropdownButton
                        title={<FaColumns />}
                        alignRight
                        as={ButtonGroup}
                        variant="default"
                      >
                        <Dropdown.Header>Sichtbare Spalten</Dropdown.Header>
                        {columns.map((col) => {
                          if (col.field === null || col.field === undefined) {
                            return null;
                          }
                          return (
                            <Dropdown.Item
                              key={col.field}
                              eventKey={col.field}
                              onClick={() => toggleColumn(col.field)}
                            >
                              {(col.hidden === undefined ||
                                col.hidden === false) && (
                                <IconText
                                  icon={<FaRegCheckSquare />}
                                  text={
                                    typeof col.name === "function"
                                      ? col.name()
                                      : col.name
                                  }
                                />
                              )}
                              {col.hidden === true && (
                                <IconText
                                  icon={<FaRegSquare />}
                                  text={
                                    typeof col.name === "function"
                                      ? col.name()
                                      : col.name
                                  }
                                />
                              )}
                            </Dropdown.Item>
                          );
                        })}
                      </DropdownButton>
                    )}
                  </ButtonGroup>
                )}
              </InputGroup>
            </Col>
          </Row>
        )}

        <div className="table-responsive add-margin">
          <table
            className="table table-bootstrap table-bordered table-hover"
            style={{ fontSize: "14px", marginBottom: 0 }}
          >
            <thead>
              {getHeaderGroupRow()}
              {getHeaderRow()}
              {getHeaderFilterRow()}
            </thead>
            {showLoading === true && isLoading === true && (
              <tbody>
                <tr>
                  <td colSpan={columns.length}>Wird geladen...</td>
                </tr>
              </tbody>
            )}
            {(showLoading === false ||
              (showLoading === true && isLoading === false)) && (
              <React.Fragment>
                <tbody>
                  {data.total <= 0 && (
                    <tr>
                      <td colSpan={columns.length}>Keine Daten</td>
                    </tr>
                  )}
                  {data.total > 0 &&
                    data.rows.map((row) => (
                      <tr>
                        {columns.map((col) => {
                          if (col.hidden === true) {
                            return null;
                          }

                          let columnValue = "-";

                          if (col.field) {
                            if (col.field.indexOf(".") !== -1) {
                              const fieldNames = col.field.split(".");

                              let lastObj = row[fieldNames[0]];

                              for (let i = 1; i < fieldNames.length; i += 1) {
                                if (lastObj[fieldNames[i]] == null) {
                                  break;
                                }
                                lastObj = lastObj[fieldNames[i]];
                              }

                              columnValue = lastObj;
                            } else {
                              columnValue = row[col.field];
                            }
                          }

                          let rowStyle = {};

                          if (col.cellStyle) {
                            rowStyle = col.cellStyle(columnValue, row);
                          }

                          // Set column width
                          // if (col.width) {
                          //   rowStyle.style = {
                          //     ...rowStyle.style,
                          //     width: col.width,
                          //   };
                          // }

                          // Set text alignment
                          if (col.textAlign) {
                            rowStyle.style = {
                              ...rowStyle.style,
                              textAlign: col.textAlign,
                            };
                          }

                          if (col.formatter) {
                            columnValue = col.formatter(columnValue, row);
                          }

                          return <td {...rowStyle}>{columnValue}</td>;
                        })}
                      </tr>
                    ))}
                </tbody>
                {getFooterRow()}
              </React.Fragment>
            )}
          </table>
        </div>
        <Row style={{ fontSize: "14px" }}>
          <Col xs="auto">
            {data.total > 0 && (
              <React.Fragment>
                {!hidePageCount && (
                  <React.Fragment>
                    Zeige{" "}
                    {(controlData.currentPage - 1) * controlData.pageSize + 1}{" "}
                    bis {controlData.currentPage * controlData.pageSize} von{" "}
                    {data.total} Einträgen{" "}
                  </React.Fragment>
                )}
                {hidePageSize === false && pageSizes.length > 0 && (
                  <DropdownButton
                    title={controlData.pageSize}
                    alignRight
                    as={ButtonGroup}
                    variant="outline-secondary"
                  >
                    <Dropdown.Header>Elemente pro Seite</Dropdown.Header>
                    {pageSizes.map((currPageSize) => (
                      <Dropdown.Item
                        key={currPageSize}
                        eventKey={currPageSize}
                        active={currPageSize === controlData.pageSize}
                        onClick={() => handlePageSize(currPageSize)}
                      >
                        {currPageSize}
                      </Dropdown.Item>
                    ))}
                  </DropdownButton>
                )}
              </React.Fragment>
            )}
          </Col>
          <Col className="text-right">
            {data.total > 0 && hidePagination === false && getPagination()}
          </Col>
        </Row>
      </React.Fragment>
    );
  }
);

BootstrapTable.propTypes = {
  id: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.func,
      ]),
      field: PropTypes.string,
      textAlign: PropTypes.oneOf(["left", "center", "right"]),
      width: PropTypes.string,
      formatter: PropTypes.func,
      cellStyle: PropTypes.func,
      footerFormatter: PropTypes.func,
      hidden: PropTypes.bool,
      sortable: PropTypes.bool,
      filterControl: PropTypes.oneOf(["input"]),
    })
  ).isRequired,
  sort: PropTypes.string,
  order: PropTypes.oneOf(["asc", "desc"]),
  pageSize: PropTypes.number,
  pageSizes: PropTypes.arrayOf(PropTypes.number),
  cache: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  searchTimeout: PropTypes.number,
  showClear: PropTypes.bool,
  showSearch: PropTypes.bool,
  showFilter: PropTypes.bool,
  showFooter: PropTypes.bool,
  showColumnToggle: PropTypes.bool,
  showRefresh: PropTypes.bool,
  hidePageSize: PropTypes.bool,
  hidePageCount: PropTypes.bool,
  hidePagination: PropTypes.bool,
  showLoading: PropTypes.bool,
  toolbar: PropTypes.node,
};

BootstrapTable.defaultProps = {
  sort: "", // field to sort by
  order: "asc", // sort order (asc or desc)
  pageSize: 15, // selected page size
  pageSizes: [10, 15, 25, 50], // available page sizes to choose from
  cache: false, // if true, state is saved in cookies and restored on page refresh
  searchPlaceholder: "search...", // Placeholder of the search input field
  searchTimeout: 700, // Delay in ms before search is triggered
  showClear: false, // show the clear-icon
  showSearch: false, // show search imput field
  showFilter: false, // show column filters
  showFooter: false, // show a footer
  showColumnToggle: false, // show hide/show function for columns
  showRefresh: false, // show refresh button
  hidePageSize: false, // show page size changer in toolbar
  hidePageCount: false, // show page count in toolbar
  hidePagination: false, // disable the pagination
  showLoading: false, // show loading between data refreshes
  toolbar: null, // Custom code to be added on the left side of the toolbar
};

BootstrapTable.displayName = "BootstrapTable";

export default BootstrapTable;
